import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import {
  FileText,
  Download,
  Eye,
  CheckCircle,
  Clock,
  Calendar,
  IndianRupee,
  XCircle,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

interface Payslip {
  _id: string;
  employeeId: string;
  month: number;
  year: number;
  netSalary: number;
  salaryComponents: {
    basic: number;
    hra: number;
    otherAllowances: number;
    deductions: {
      pf: number;
      esi: number;
    };
    total: number;
  };
  attendanceData: {
    workingDays: number;
    paidLeaves: number;
    unpaidLeaves: number;
  };
  status: "pending" | "approved";
  s3Url?: string;
  createdAt: string;
}

export default function EmployeePayslips() {
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [filteredPayslips, setFilteredPayslips] = useState<Payslip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<string>("all");

  useEffect(() => {
    fetchPayslips();
  }, []);

  useEffect(() => {
    filterPayslips();
  }, [yearFilter, payslips]);

  const fetchPayslips = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/me/payslips`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPayslips(response.data);
      setFilteredPayslips(response.data);
    } catch (error: any) {
      console.error("Error fetching payslips:", error);
      setError(error.response?.data?.message || "Failed to fetch payslips");
    } finally {
      setIsLoading(false);
    }
  };

  const filterPayslips = () => {
    if (yearFilter === "all") {
      setFilteredPayslips(payslips);
    } else {
      setFilteredPayslips(
        payslips.filter((p) => p.year.toString() === yearFilter)
      );
    }
  };

  const handleDownloadPayslip = async (payslip: Payslip) => {
    if (!payslip.s3Url) {
      alert("Payslip PDF not available");
      return;
    }

    try {
      setDownloadingId(payslip._id);
      
      // Fetch the file as a blob to force download instead of opening in new tab
      const token = localStorage.getItem("authToken");
      let response;
      
      try {
        // Try with authentication header first
        response = await axios.get(payslip.s3Url, {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (authError) {
        // If that fails, try without auth (for pre-signed URLs or public URLs)
        response = await axios.get(payslip.s3Url, {
          responseType: "blob",
        });
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `payslip_${payslip.month}_${payslip.year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading payslip:", error);
      alert("Failed to download payslip. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewPayslip = (payslip: Payslip) => {
    if (!payslip.s3Url) {
      alert("Payslip PDF not available");
      return;
    }
    window.open(payslip.s3Url, "_blank");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getMonthName = (month: number) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[month - 1];
  };

  const getStatusBadge = (status: string) => {
    if (status === "approved") {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="mr-1 h-3 w-3" />
          Approved
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
        <Clock className="mr-1 h-3 w-3" />
        Pending
      </Badge>
    );
  };

  const getUniqueYears = () => {
    const uniqueYearsSet = new Set(payslips.map((p) => p.year));
    const years = Array.from(uniqueYearsSet);
    return years.sort((a, b) => b - a);
  };

  const calculateTotalEarned = () => {
    return filteredPayslips
      .filter((p) => p.status === "approved")
      .reduce((sum, p) => sum + p.netSalary, 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">
            Loading your payslips...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Payslips</h1>
          <p className="text-muted-foreground mt-1">
            View and download your monthly payslips
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {payslips.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Payslips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <span className="text-2xl font-bold">
                  {filteredPayslips.length}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Approved Payslips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold">
                  {
                    filteredPayslips.filter((p) => p.status === "approved")
                      .length
                  }
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Earned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <IndianRupee className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold">
                  {formatCurrency(calculateTotalEarned())}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                From approved payslips
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      {payslips.length > 0 && (
        <div className="mb-6">
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {getUniqueYears().map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Payslips Grid */}
      {filteredPayslips.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPayslips.map((payslip) => (
            <Card key={payslip._id} className="transition-all hover:shadow-lg">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base">
                        {getMonthName(payslip.month)} {payslip.year}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        Payslip #{payslip._id.slice(-6).toUpperCase()}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(payslip.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Net Salary
                    </span>
                    <span className="text-lg font-bold text-green-600">
                      {formatCurrency(payslip.netSalary)}
                    </span>
                  </div>

                  <div className="pt-3 border-t space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        Working Days
                      </span>
                      <span className="font-medium">
                        {payslip.attendanceData.workingDays}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Paid Leaves</span>
                      <span className="font-medium">
                        {payslip.attendanceData.paidLeaves}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        Unpaid Leaves
                      </span>
                      <span className="font-medium">
                        {payslip.attendanceData.unpaidLeaves}
                      </span>
                    </div>
                  </div>

                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        Generated on
                      </span>
                      <span className="text-muted-foreground">
                        {new Date(payslip.createdAt).toLocaleDateString(
                          "en-IN"
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {payslip.s3Url ? (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleViewPayslip(payslip)}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => handleDownloadPayslip(payslip)}
                      disabled={downloadingId === payslip._id}
                    >
                      {downloadingId === payslip._id ? (
                        <>
                          <Spinner
                            variant="ellipsis"
                            size={16}
                            className="mr-2"
                          />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">
                      PDF not available yet
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : payslips.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">
                No Payslips Available
              </h3>
              <p className="text-muted-foreground mb-4">
                You don't have any payslips generated yet.
              </p>
              <p className="text-sm text-muted-foreground">
                Payslips are generated monthly by the admin.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Calendar className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">
                No Payslips for {yearFilter}
              </h3>
              <p className="text-muted-foreground mb-4">
                No payslips found for the selected year.
              </p>
              <Button variant="outline" onClick={() => setYearFilter("all")}>
                View All Payslips
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
